import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { BiChevronDown, BiPlus } from "react-icons/bi";
import { Button, Input, Select } from "../Form";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchVacunasCatalog,
  createVaccineOrder,
  selectVacunasCatalog,
  selectVaccineOrderLoading,
  selectVaccineOrderError,
} from "../../redux/features/vaccineOrderSlice";
import { toast } from "react-hot-toast";

const dosisOptions = [
  { id: 1, name: "Primera dosis" },
  { id: 2, name: "Segunda dosis" },
  { id: 3, name: "Tercera dosis" },
  { id: 4, name: "Refuerzo" },
  { id: 5, name: "Única dosis" },
];

function VaccineOrderModal({
  closeModal,
  isOpen,
  setVaccineOrder,
  visitaId,
  patientId,
}) {
  const [dosis, setDosis] = useState(dosisOptions[0]);
  const [filteredData, setFilteredData] = useState([]);
  const [vacunaSeleccionada, setVacunaSeleccionada] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      vacunaId: "",
      nombre: "",
      fechaAplicacion: new Date().toISOString().split("T")[0],
      dosis: "Primera dosis",
      proximaDosis: "",
      observaciones: "",
    },
  });

  const dispatch = useDispatch();
  const vacunasCatalog = useSelector(selectVacunasCatalog);
  const loadingCatalog = useSelector(selectVaccineOrderLoading);
  const errorCatalog = useSelector(selectVaccineOrderError);

  // Cargar catálogo de vacunas al abrir el modal
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchVacunasCatalog());
    }
  }, [isOpen, dispatch]);

  const handleAgregar = async (formData) => {
    if (!vacunaSeleccionada) {
      toast.error("Debe seleccionar una vacuna");
      return;
    }

    const vaccineData = {
      visitaId: visitaId,
      pacienteId: patientId,
      vacuna: vacunaSeleccionada.nombre,
      tipoDosis: formData.dosis,
      fechaAplicacion: formData.fechaAplicacion,
      observaciones: formData.observaciones || null,
    };

    try {
      const resultAction = await dispatch(createVaccineOrder(vaccineData));

      if (createVaccineOrder.fulfilled.match(resultAction)) {
        const nuevaVacuna = resultAction.payload;

        setVaccineOrder((prev) => [
          ...prev,
          {
            ...nuevaVacuna,
            nombre: vacunaSeleccionada.nombre,
            vacuna: vacunaSeleccionada,
          },
        ]);

        toast.success("Vacuna guardada correctamente");
        closeModal();
      } else {
        throw new Error(resultAction.error.message);
      }
    } catch (error) {
      console.error("Error al guardar vacuna:", error);
      toast.error(`Error al guardar: ${error.message}`);
    }
  };

  const handleChange = (e) => {
    const searchTerm = e.target.value.toLowerCase();

    if (searchTerm.length === 0) {
      setFilteredData([]);
      setVacunaSeleccionada(null);
      setValue("vacunaId", "");
      return;
    }

    const filtered = vacunasCatalog
      .filter((item) => item.nombre.toLowerCase().includes(searchTerm))
      .slice(0, 10);

    setFilteredData(filtered);
  };

  const handleSelectVacuna = (vacuna) => {
    setValue("vacunaId", vacuna.id);
    setValue("nombre", vacuna.nombre);
    setVacunaSeleccionada(vacuna);
    setFilteredData([]);
  };

  return (
    <Modal
      closeModal={closeModal}
      isOpen={isOpen}
      title="Agregar Vacuna a la Orden"
      width="max-w-xl"
    >
      <form
        onSubmit={handleSubmit(handleAgregar)}
        className="flex flex-col gap-6"
      >
        {/* Campo de búsqueda de vacuna */}
        <div className="w-full relative">
          <Input
            label="Vacuna"
            color={true}
            register={register("nombre", {
              required: "El nombre de la vacuna es obligatorio",
              onChange: handleChange,
              onBlur: () => setTimeout(() => setFilteredData([]), 200),
            })}
            type="text"
            error={errors.nombre}
            disabled={loadingCatalog}
          />

          {filteredData.length > 0 && (
            <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredData.map((item) => (
                <li
                  key={item.id}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                  onMouseDown={() => handleSelectVacuna(item)}
                >
                  {item.nombre} ({item.codigo})
                </li>
              ))}
            </ul>
          )}
          <input type="hidden" {...register("vacunaId", { required: true })} />
          {errors.vacunaId && (
            <p className="text-red-500 text-xs mt-1">
              Debe seleccionar una vacuna
            </p>
          )}
        </div>

        {vacunaSeleccionada && (
          <div className="bg-blue-50 p-3 rounded-lg">
            <h4 className="font-medium text-blue-800">Vacuna seleccionada:</h4>
            <p className="text-sm">
              <span className="font-medium">Código:</span>{" "}
              {vacunaSeleccionada.codigo}
            </p>
            <p className="text-sm">
              <span className="font-medium">Fabricante:</span>{" "}
              {vacunaSeleccionada.fabricante}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <label className="text-sm text-gray-700">Tipo de dosis</label>
          <Select
            selectedPerson={dosis}
            setSelectedPerson={(selected) => {
              setDosis(selected);
              setValue("dosis", selected.name);
            }}
            datas={dosisOptions}
          >
            <div className="w-full flex items-center justify-between text-gray-600 text-sm p-3 border border-gray-300 rounded-lg hover:border-blue-500 transition-colors">
              <span>{dosis.name}</span>
              <BiChevronDown className="text-xl" />
            </div>
          </Select>
        </div>

        <Input
          label="Fecha de aplicación"
          color={true}
          register={register("fechaAplicacion", {
            required: "La fecha de aplicación es obligatoria",
          })}
          type="date"
          error={errors.fechaAplicacion}
          disabled={loadingCatalog}
        />

        <div className="flex flex-col gap-2">
          <label className="text-sm text-gray-700">
            Observaciones (opcional)
          </label>
          <textarea
            {...register("observaciones")}
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
            rows={3}
            placeholder="Notas adicionales sobre la aplicación..."
            disabled={loadingCatalog}
          />
        </div>

        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={closeModal}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={loadingCatalog}
          >
            Cancelar
          </button>
          <Button
            type="submit"
            label={loadingCatalog ? "Guardando..." : "Agregar Vacuna"}
            Icon={BiPlus}
            loading={loadingCatalog}
            disabled={loadingCatalog}
          />
        </div>
      </form>
    </Modal>
  );
}

export default VaccineOrderModal;
